import axios, { AxiosResponse } from 'axios';

import { getApiEndPoint } from '../helpers/apibase';
import { getFeatureFlagValue } from './featureFlags/getFeatureFlagValue';
import { PortalFeatureFlags } from './featureFlags/portalFeatureFlags';
import { mapServerError } from './utils/serverErrorMapper';

// This is a very hacky way to get the URL
// But we had typecheck errors otherwise with constants.json
const isProd = window.location?.host?.includes('portal.flipdish.com');
const financeApiBaseUrl = isProd
  ? 'https://api.portal.flipdish.com/finance'
  : 'https://finance-integration.portal.flipdishdev.com';

export interface ValidateBankAccountRequest {
  countryCode: string;
  iban?: string;
  accountNumber?: string;
  sortCode?: string;
}
export interface ValidateBankAccountResponse {
  valid: boolean;
  errorMessage?: string;
  bankName?: string;
  bankAddress?: string;
  bic?: string;
  iban?: string;
}

const financeApi = axios.create({
  baseURL: financeApiBaseUrl,
  withCredentials: true,
});

// Bank account validation is moving from finance to business-details (CE-1314). The
// bankAccount_validateWithBusinessDetails flag picks the backend; off (the default) keeps finance.
const businessDetailsApi = axios.create({
  baseURL: getApiEndPoint(),
  withCredentials: true,
});

// From the official page: https://www.iban.com/structure
const ibanLengthByCountryCode: Record<string, number> = {
  AL: 28, // Albania
  AD: 24, // Andorra
  AT: 20, // Austria
  AZ: 28, // Azerbaijan
  BH: 22, // Bahrain
  BE: 16, // Belgium
  BA: 20, // Bosnia and Herzegovina
  BR: 29, // Brazil
  BG: 22, // Bulgaria
  CR: 22, // Costa Rica
  HR: 21, // Croatia
  CY: 28, // Cyprus
  CZ: 24, // Czech Republic
  DK: 18, // Denmark
  DO: 28, // Dominican Republic
  EE: 20, // Estonia
  EG: 29, // Egypt
  FO: 18, // Faroe Islands
  FI: 18, // Finland
  FR: 27, // France
  GE: 22, // Georgia
  DE: 22, // Germany
  GI: 23, // Gibraltar
  GR: 27, // Greece
  GL: 18, // Greenland
  GT: 28, // Guatemala
  HU: 28, // Hungary
  IS: 26, // Iceland
  IE: 22, // Ireland
  IL: 23, // Israel
  IT: 27, // Italy
  JO: 30, // Jordan
  KZ: 20, // Kazakhstan
  XK: 20, // Kosovo
  KW: 30, // Kuwait
  LV: 21, // Latvia
  LB: 28, // Lebanon
  LI: 21, // Liechtenstein
  LT: 20, // Lithuania
  LU: 20, // Luxembourg
  MK: 19, // North Macedonia
  MT: 31, // Malta
  MR: 27, // Mauritania
  MU: 30, // Mauritius
  MD: 24, // Moldova
  MC: 27, // Monaco
  ME: 22, // Montenegro
  NL: 18, // Netherlands
  NO: 15, // Norway
  PK: 24, // Pakistan
  PS: 29, // Palestine
  PL: 28, // Poland
  PT: 25, // Portugal
  QA: 29, // Qatar
  RO: 24, // Romania
  SM: 27, // San Marino
  LC: 32, // Saint Lucia
  ST: 25, // Sao Tome and Principe
  SA: 24, // Saudi Arabia
  RS: 22, // Serbia
  SK: 24, // Slovakia
  SI: 19, // Slovenia
  ES: 24, // Spain
  SE: 24, // Sweden
  CH: 21, // Switzerland
  TL: 23, // Timor-Leste
  TN: 24, // Tunisia
  TR: 26, // Turkey
  AE: 23, // United Arab Emirates
  GB: 22, // United Kingdom
  VA: 22, // Vatican City
  VG: 24, // Virgin Islands, British
  UA: 29, // Ukraine
  SC: 31, // Seychelles
  IQ: 23, // Iraq
  BY: 28, // Belarus
  SV: 28, // El Salvador
  LY: 25, // Libya
  SD: 18, // Sudan
  BI: 27, // Burundi
  DJ: 27, // Djibouti
  RU: 33, // Russia
  SO: 23, // Somalia
  NI: 28, // Nicaragua
  MN: 20, // Mongolia
  FK: 18, // Falkland Islands
  OM: 23, // Sultanate of Oman
  YE: 30, // Yemen
  HN: 28, // Honduras
  // Experimental IBAN countries
  AO: 25, // Angola
  BF: 28, // Burkina Faso
  BJ: 28, // Benin
  CF: 27, // Central African Republic
  CG: 27, // Congo
  CI: 28, // Ivory Coast
  CM: 27, // Cameroon
  CV: 25, // Cape Verde
  DZ: 26, // Algeria
  GA: 27, // Gabon
  GQ: 27, // Equatorial Guinea
  GW: 25, // Guinea-Bissau
  IR: 26, // Iran
  MA: 28, // Morocco
  MG: 27, // Madagascar
  ML: 28, // Mali
  MZ: 25, // Mozambique
  NE: 28, // Niger
  SN: 28, // Senegal
  TD: 27, // Chad
  TG: 28, // Togo
  KM: 27, // Comoros
};

export const validateBankAccount = async (
  orgId: string,
  validateBankAccountRequest: ValidateBankAccountRequest
): Promise<ValidateBankAccountResponse> => {
  try {
    if (validateBankAccountRequest.iban && validateBankAccountRequest.iban.length > 2) {
      const countryCodeFromIban = validateBankAccountRequest.iban.substring(0, 2);
      const expectedLength = ibanLengthByCountryCode[countryCodeFromIban];
      if (expectedLength && validateBankAccountRequest.iban.length !== expectedLength) {
        return {
          valid: false,
          errorMessage: `IBAN has invalid length (country code from IBAN: ${countryCodeFromIban}, expected: ${expectedLength}, received: ${validateBankAccountRequest.iban.length})`,
        };
      }
    }
    if (
      validateBankAccountRequest.countryCode === 'GB' &&
      validateBankAccountRequest.sortCode &&
      validateBankAccountRequest.sortCode?.length !== 6
    ) {
      return {
        valid: false,
        errorMessage: `Sort code is too short (expected length: 6)`,
      };
    }
    const response = getFeatureFlagValue(
      PortalFeatureFlags.bankAccount_validateWithBusinessDetails,
      false
    )
      ? await businessDetailsApi.post<
          ValidateBankAccountRequest,
          AxiosResponse<ValidateBankAccountResponse>
        >(
          `/business-details/orgs/${encodeURIComponent(orgId)}/bank-accounts/validations`,
          validateBankAccountRequest
        )
      : await financeApi.post<
          ValidateBankAccountRequest,
          AxiosResponse<ValidateBankAccountResponse>
        >(`/orgs/${orgId}/bankAccountValidator`, validateBankAccountRequest);
    if (response.status !== 200) {
      const err = await mapServerError(response);
      throw err;
    }
    return response.data;
  } catch (error) {
    const err = await mapServerError(error);
    throw err;
  }
};
